import { queryOptions } from '@tanstack/react-query';
import {
  CatalogPropertiesService,
  type CatalogPropertyPreview,
  type CatalogPropertyPreviewsResponse,
} from 'src/api/generated';
import type {
  PropertyPreviewAccessContext,
  PropertyPreviewLoader,
  PropertyPreviewQueryKey,
} from 'src/modules/shared/types/api/property-preview.type';
import { awaitCancelableRequest } from 'src/modules/shared/utils/api/cancelable-request.util';

/** Successful photo freshness, separate from inactive cache retention. */
export const PROPERTY_PREVIEW_FRESHNESS_MS = 15 * 60 * 1000;
/** Definitive missing properties or photos are revalidated promptly. */
export const PROPERTY_PREVIEW_NULL_FRESHNESS_MS = 60 * 1000;
/** Inactive previews survive view switches and ordinary remounts. */
export const PROPERTY_PREVIEW_GC_MS = 30 * 60 * 1000;

/**
 * Builds the per-property catalog key used by all preview observers.
 * @param context - Current effective tenant, user and read grants.
 * @param propertyId - Linked property ID.
 * @returns A key under the existing catalog mutation invalidation prefix.
 */
export const propertyPreviewQueryKey = (
  context: PropertyPreviewAccessContext,
  propertyId: number
): PropertyPreviewQueryKey => [
  'catalog/properties',
  'preview',
  context.tenantId,
  context.userId,
  context.permissionKey,
  propertyId,
];

/**
 * Calculates freshness from the original fetch time, independently of cache reads.
 * @param preview - The successfully fetched entry.
 * @param updatedAt - React Query's original successful fetch timestamp.
 * @returns The entry's freshness deadline in epoch milliseconds.
 */
export const propertyPreviewFreshUntil = (
  preview: CatalogPropertyPreview | undefined,
  updatedAt: number
): number => {
  const freshness = preview?.property?.imageUrl
    ? PROPERTY_PREVIEW_FRESHNESS_MS
    : PROPERTY_PREVIEW_NULL_FRESHNESS_MS;
  return updatedAt + freshness;
};

/**
 * Supplies per-property query options; retry/error ownership stays with its loader.
 * @param context - Current access context.
 * @param propertyId - Linked property ID.
 * @param loader - The view-owned batch loader.
 * @param cachedPreview - Existing cache data, when present.
 * @param updatedAt - Existing successful fetch timestamp.
 * @returns Options for the existing QueryClient, with no per-property retry loop.
 */
export const propertyPreviewQueryOptions = (
  context: PropertyPreviewAccessContext,
  propertyId: number,
  loader: PropertyPreviewLoader,
  cachedPreview?: CatalogPropertyPreview,
  updatedAt = 0
) =>
  queryOptions({
    queryKey: propertyPreviewQueryKey(context, propertyId),
    queryFn: ({ signal }) => loader(propertyId, signal),
    staleTime: cachedPreview
      ? propertyPreviewFreshUntil(cachedPreview, updatedAt) - updatedAt
      : PROPERTY_PREVIEW_NULL_FRESHNESS_MS,
    gcTime: PROPERTY_PREVIEW_GC_MS,
    retry: false,
    meta: { errorHandling: 'caller' },
  });

/**
 * Calls the generated bulk catalog endpoint, skipping empty requests.
 * @param propertyIds - One bounded batch of distinct positive property IDs.
 * @param signal - Cancellation signal owned by that HTTP attempt.
 * @returns The canonical bulk response.
 * @throws The original generated API error or cancellation.
 */
export const requestPropertyPreviews = async (
  propertyIds: number[],
  signal: AbortSignal
): Promise<CatalogPropertyPreviewsResponse> => {
  if (propertyIds.length === 0) return { items: [] };

  return await awaitCancelableRequest(
    CatalogPropertiesService.getPropertyPreviews({ propertyIds }),
    signal
  );
};
